import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './PostComposer.css';
import { toast } from 'react-toastify';
import { addFeed, addPolicy } from '../../../services/Trainer/trainer';
import { DescriptionEditor } from '../policyManagement/DescriptionEditor';

const postTypes = {
  policy: 'Policy',
  announcement: 'Announcement',
  news: 'News',
};

const validationSchemas = {
  policy: Yup.object({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
  }),
  announcement: Yup.object({
    description: Yup.string().required('Description is required'),
    image: Yup.mixed().required('Cover image is required'),
  }),
  news: Yup.object({
    description: Yup.string().required('Description is required'),
    image: Yup.mixed().required('Cover image is required'),
  }),
};

const PostComposer = ({ userAvatar, userName = 'User', fetchData }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('news');
  const [loading, setLoading] = useState(false);

const handleSubmit = async (values, { resetForm }) => {
  setLoading(true);

  // clone description so we can replace blob src
  let descriptionHTML = values.description || "";
  console.log("👉 Final values>>>",values);

  const formData = new FormData();
  formData.append("title", values.title || "");
  formData.append("type", modalType);

  // replace blob urls in description with image_1, image_2...
  if (Array.isArray(values.images)) {
    values.images.forEach((imgObj, index) => {
      const { file, blobUrl } = imgObj; // 👈 we need blobUrl saved when inserting
      const imageKey = `image_${index + 1}`;

      // replace the blob url in HTML with image key
      descriptionHTML = descriptionHTML.replace(blobUrl, imageKey);

      // append file to formData
      formData.append(imageKey, file);
    });
  }
  if (values.image) {
      formData.append('file', values.image);
    }

  // replace file urls in description with doc_1, doc_2...
  if (Array.isArray(values.documents)) {
    values.documents.forEach((docObj, index) => {
      const { file, blobUrl } = docObj;
      const docKey = `doc_${index + 1}`;
      descriptionHTML = descriptionHTML.replace(blobUrl, docKey);
      formData.append(docKey, file);
    });
  }

  // finally add description
  formData.append("description", descriptionHTML);

  // ✅ debug console
  console.log("👉 Final description:",modalType, descriptionHTML);
  for (let pair of formData.entries()) {
    console.log(pair[0], pair[1]);
  }
setLoading(false);
  try {
    let result;
    if (modalType === "policy") {
      result = await addPolicy(formData);
    } else {
      result = await addFeed(formData);
    }

    if (result?.success) {
      toast.success(result?.message);
      fetchData?.();
    } else {
      toast.error(result?.message);
    }
  } catch (err) {
    console.error("Failed to submit post:", err);
  } finally {
    setLoading(false);
    setShowModal(false);
    resetForm();
  }
};




  const renderModal = () => {
    if (!showModal) return null;

    const modalConfig = {
      policy: { title: 'Create Policy Post', icon: 'bi-file-text', color: 'primary' },
      announcement: { title: 'Make Announcement', icon: 'bi-megaphone', color: 'warning' },
      news: { title: 'Share News', icon: 'bi-newspaper', color: 'success' },
    };

    const config = modalConfig[modalType];
    const isImageRequired = modalType !== 'policy';

    return (
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <Formik
              initialValues={{
                title: '',
                description: '',
                image: null,
                images: [],
                documents: [],
              }}
              validationSchema={validationSchemas[modalType]}
              onSubmit={handleSubmit}
            >
              {({ setFieldValue, isSubmitting, values }) => (
                <Form>
                  <div className="modal-header">
                    <h5 className="modal-title d-flex align-items-center gap-2">
                      <i className={`bi ${config.icon}`}></i> {config.title}
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Post Type</label>
                      <select
                        className="form-select"
                        value={modalType}
                        onChange={(e) => setModalType(e.target.value)}
                      >
                        {Object.entries(postTypes).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="d-flex gap-3 mb-3">
                      <div className="profile-avatar">
                        {userAvatar ? (
                          <img src={userAvatar} alt={userName} className="rounded-circle" />
                        ) : (
                          <div className="profile-circle">{userName.charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <strong>{userName}</strong>
                        <div className="text-muted small">Creating {postTypes[modalType]} post</div>
                      </div>
                    </div>

                    {modalType === 'policy' && (
                      <div className="form-group mb-4">
                        <Field
                          name="title"
                          type="text"
                          placeholder="Policy Title"
                          className="custom-input"
                        />
                        <ErrorMessage name="title" component="div" className="error-text" />
                      </div>
                    )}

                    <div className="form-group mb-4">
                      <label>{modalType === 'policy' ? 'Policy Description' : 'Post Description'}</label>
                      <DescriptionEditor
                      value={values.description}
                      onChange={(val) => setFieldValue('description', val)}
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                      <ErrorMessage name="description" component="div" className="error-text" />
                    </div>

                    {isImageRequired && (
                      <div className="mb-3">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(e) => setFieldValue('image', e.currentTarget.files[0])}
                        />
                        <ErrorMessage name="image" component="div" className="text-danger mt-1" />
                        <small className="text-muted">Cover image (separate from editor images)</small>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className={`btn btn-${config.color}`} disabled={isSubmitting || loading}>
                      {loading ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="card post-composer-card mb-3 rounded">
        <div className="card-body p-3">
          <div className="d-flex gap-3 mb-3">
            <div className="profile-avatar">
              {userAvatar ? (
                <img src={userAvatar} alt={userName} className="rounded-circle" />
              ) : (
                <div className="profile-circle">{userName.charAt(0).toUpperCase()}</div>
              )}
            </div>
            <div className="flex-grow-1">
              <textarea
                className="form-control post-input"
                placeholder="Write here"
                rows={1}
                onFocus={() => {
                  setModalType('news');
                  setShowModal(true);
                }}
                readOnly
                style={{ padding: '15px', borderRadius: '20px' }}
              />
            </div>
          </div>

          <div className="d-flex justify-content-around">
            <button className="btn post-type-btn" onClick={() => { setModalType('policy'); setShowModal(true); }}>
              <i className="bi bi-file-text me-2"></i> Policies
            </button>
            <button className="btn post-type-btn" onClick={() => { setModalType('announcement'); setShowModal(true); }}>
              <i className="bi bi-megaphone me-2"></i> Announcement
            </button>
            <button className="btn post-type-btn" onClick={() => { setModalType('news'); setShowModal(true); }}>
              <i className="bi bi-newspaper me-2"></i> News
            </button>
          </div>
        </div>
      </div>

      {renderModal()}
    </>
  );
};

export default PostComposer;
